import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { useNavigate } from 'react-router-dom';
import { useCurrentInternalUser } from '../lib/currentUser';
import Sidebar from '../components/Sidebar';
import MoneyInput from '../components/MoneyInput';
import AutoSizeTextarea from '../components/AutoSizeTextarea';
import BackIconButton from '../components/BackIconButton';

const today = () => new Date().toISOString().split('T')[0];

export default function AddSupplier() {
  const navigate = useNavigate();
  const { user: currentUser } = useCurrentInternalUser();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [categories, setCategories] = useState([]);
  const [regions, setRegions] = useState([]);
  const [activeTab, setActiveTab] = useState('fusion');
  const [form, setForm] = useState({
    // Tab 1 — requestor (auto-populated below from currentUser)
    requestor_name: '', requestor_job_title: '', requestor_department: '', date_of_request: today(),
    // Tab 1 — supplier identity + contacts (compulsory marked *)
    supplier_name: '',
    main_contact_name: '', main_contact_phone: '', main_contact_email: '',
    secondary_contact_name: '', secondary_contact_phone: '', secondary_contact_email: '',
    // Tab 1 — business context
    goods_services_description: '', reason_for_new_supplier: '', impact_if_not_setup: '', who_evaluated: '',
    // Tab 1 — project & spend
    initial_project_cost_centre: '', estimated_first_order: '', estimated_annual_spend: '', expected_duration: '',
    // Tab 1 — commercial
    payment_terms: '',
    // Tab 2 — existing supplier fields
    account_code: '', address_line_1: '', address_line_2: '', address_line_3: '', town: '', postcode: '',
    region: '', phone: '', website: '',
    category: '', sub_category: '', analysis_code_1: '', analysis_code_2: '',
    company_size: '', source: '', do_not_use_status: '', preferred_supplier: '',
    is_record_only: false,
    new_supplier_form_received: '', nda_signed: '', hs_questionnaire_returned: '',
    code_of_conduct_returned: '',
    rating_general: '', rating_price: '', rating_quality: '',
    notes_general: '', notes_previous_work: '', notes_relationship: '',
    fab_carpentry: false, fab_metalwork: false, fab_solid_surface: false,
    fab_print_graphics: false, fab_cnc_cutting: false, fab_wet_spraying: false,
    fab_plastics: false, fab_3d_printing: false, fab_development_capability: false,
    fab_dmx: false, fab_electrical_engineering: false, fab_meu_coachbuilding: false,
    venue_high_footfall: false, venue_test_drive_events: false, venue_press_launch: false,
    venue_meeting_room_sizes: '', venue_number_of_bedrooms: '', venue_test_drive_environment: '',
    venue_preferred_suppliers: '', venue_access_information: '', venue_onsite_rules: '',
    venue_automotive_permitted: '', venue_power: '', venue_external_water_power: '',
    venue_ev_chargers: '', venue_vehicles_displayed_inside: '', venue_inc_ex_vat: '',
    venue_commissionable_items: '', venue_commission_percentage: '', venue_ddr_rate: '',
    venue_ddr_inclusions: '', venue_24hr_rate: '', venue_24hr_inclusions: '',
    venue_car_parking: '', venue_car_parking_cost: '', venue_additional_catering: '',
    venue_space_hire_rate: '', venue_name_of_space: '', venue_av_inclusions: '',
    venue_cost_additional_av: '', venue_valeting_area: '', venue_type: '',
    venue_search_criteria: '',
  });

  useEffect(() => {
    const getLists = async () => {
      const { data: cats } = await supabase.from('list_values').select('value').eq('list_name', 'supplier_category').order('display_order');
      const { data: regs } = await supabase.from('list_values').select('value').eq('list_name', 'region').order('display_order');
      setCategories(cats || []);
      setRegions(regs || []);
    };
    getLists();
  }, []);

  useEffect(() => {
    if (currentUser) {
      setForm(prev => ({
        ...prev,
        requestor_name: prev.requestor_name || currentUser.full_name || '',
        requestor_job_title: prev.requestor_job_title || currentUser.job_title || '',
        requestor_department: prev.requestor_department || currentUser.department || '',
      }));
    }
  }, [currentUser]);

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    if (!form.supplier_name) { setError('Supplier name is required'); setActiveTab('fusion'); return; }
    // Record-only suppliers can skip the contact fields and the risk flag
    // — we're not going to email them or run them through the compliance
    // workflow, so the data isn't useful here. Supplier name is the only
    // hard requirement.
    if (!form.is_record_only) {
      if (!form.main_contact_name || !form.main_contact_email || !form.main_contact_phone) {
        setError('Main contact name, email and phone are required'); setActiveTab('fusion'); return;
      }
      if (!form.payment_terms) { setError('Supplier payment terms are required'); setActiveTab('fusion'); return; }
      if (!form.risk_flag) { setError('Risk flag is required — pick High, Medium or Low'); setActiveTab('fusion'); return; }
    }
    setSaving(true);
    setError(null);
    const payload = {
      ...form,
      requestor_id: currentUser?.id || null,
      // Set on creation — risk flag dictates which compliance docs are
      // mandatory, and the supplier is active immediately (only bulk-imports
      // start as inactive, via the migration backfill).
      risk_flag_set_at: new Date().toISOString(),
      risk_flag_set_by: currentUser?.id || null,
      status: 'active',
      estimated_first_order: form.estimated_first_order === '' ? null : Number(form.estimated_first_order),
      estimated_annual_spend: form.estimated_annual_spend === '' ? null : Number(form.estimated_annual_spend),
      rating_general: form.rating_general === '' ? null : form.rating_general,
      rating_price: form.rating_price === '' ? null : form.rating_price,
      rating_quality: form.rating_quality === '' ? null : form.rating_quality,
    };
    const { data, error: insertError } = await supabase.from('suppliers').insert([payload]).select().single();
    if (insertError) { setError(insertError.message); setSaving(false); return; }
    navigate(`/suppliers/${data.id}`);
  };

  const isFabricator = form.category === 'Fabrication / Materials' || form.category === 'Fabricator';
  const isVenue = form.category === 'Venue';

  const inputClass = "w-full bg-white border border-line rounded-xl text-ink px-4 py-2.5 font-['Metropolis'] text-sm focus:outline-none focus:border-[#92D400] transition-colors";
  const readOnlyClass = "w-full bg-white border border-line rounded-xl text-fusion-mid-grey px-4 py-2.5 font-['Metropolis'] text-sm";
  const labelClass = "font-['Metropolis'] text-xs font-semibold text-[#51534A] block mb-1";
  const sectionClass = "font-['Metropolis'] text-[11px] font-semibold tracking-[0.18em] uppercase text-fusion-forest mb-4 mt-8 pb-2 border-b border-line";
  const subSectionClass = "font-['Metropolis'] text-base font-semibold text-ink mb-3 mt-6";
  const checkboxClass = "flex items-center gap-3 cursor-pointer";
  const tabClass = (tab) => `font-['Metropolis'] text-sm font-semibold px-6 py-3 transition-colors ${activeTab === tab ? 'text-fusion-forest border-b-2 border-[#92D400]' : 'text-[#51534A] hover:text-fusion-mid-grey'}`;

  return (
    <div className="h-screen bg-canvas flex overflow-hidden">

      {/* Sidebar — sticky to viewport so it stays visible while the main
          content scrolls. */}
      <Sidebar currentInternalUser={currentUser} />

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">

        {/* Page header — breadcrumb-style back link, inline H1, actions
            on the right, divider beneath. Matches the Dashboard pattern;
            the divider is inset (mx-8) so it doesn't run edge-to-edge
            across the page. */}
        <div className="pt-8 flex-shrink-0">
          <div className="px-8 mb-3">
            <BackIconButton to="/suppliers" label="Back to suppliers" />
          </div>
          <div className="px-8 flex items-start justify-between gap-6 mb-6">
            <div className="min-w-0">
              <h1 className="fusion-h1 text-fusion-forest">Add New Supplier</h1>
              <p className="font-['Metropolis'] text-sm text-fusion-mid-grey mt-2">Capture both the Fusion-internal record and the supplier's own details.</p>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0 pt-1 flex-wrap justify-end">
              {error && <p className="font-['Metropolis'] text-sm text-[#E0290A]">{error}</p>}
              <button onClick={() => navigate('/suppliers')} className="bg-white rounded-r-btn border border-line text-fusion-mid-grey font-['Metropolis'] text-sm font-semibold px-5 py-2 hover:border-[#DADCCD] transition-colors">Cancel</button>
              <button onClick={handleSave} disabled={saving} className="bg-[#92D400] text-[#131313] rounded-r-btn font-['Metropolis'] text-sm font-semibold px-5 py-2 hover:bg-[#6a9a00] transition-colors disabled:opacity-50">
                {saving ? 'Saving...' : 'Save Supplier'}
              </button>
            </div>
          </div>
          <div className="mx-8 h-px bg-[#E6E9DA]" aria-hidden="true" />
        </div>

        {/* Tab bar — inset to mx-8 so the active underline lines up
            with the page divider above. */}
        <div className="mx-8 border-b border-line flex flex-shrink-0">
          <button className={tabClass('fusion')} onClick={() => setActiveTab('fusion')}>Fusion Internal Information</button>
          <button className={tabClass('supplier')} onClick={() => setActiveTab('supplier')}>Supplier Information</button>
        </div>

        {/* Form */}
        <div className="flex-1 overflow-y-auto p-8">
          <div className="max-w-6xl">

            {activeTab === 'fusion' && (
              <>
                <p className={sectionClass}>Initial Requestor Details</p>
                <p className="font-['Metropolis'] text-xs text-[#51534A] mb-4">Auto-populated from your account. To change job title or department, update your profile in Admin.</p>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className={labelClass}>Name</label>
                    <input className={readOnlyClass} value={form.requestor_name} readOnly />
                  </div>
                  <div>
                    <label className={labelClass}>Job Title</label>
                    <input className={readOnlyClass} value={form.requestor_job_title || '—'} readOnly />
                  </div>
                  <div>
                    <label className={labelClass}>Department / Project Team</label>
                    <input className={readOnlyClass} value={form.requestor_department || '—'} readOnly />
                  </div>
                  <div>
                    <label className={labelClass}>Date of Request</label>
                    <input className={readOnlyClass} value={form.date_of_request} readOnly />
                  </div>
                </div>

                <p className={sectionClass}>Supplier Identity & Contact Details</p>
                <p className={subSectionClass}>Supplier</p>
                <div className="grid grid-cols-2 gap-6">
                  <div className="col-span-2">
                    <label className={labelClass}>Supplier Name *</label>
                    <input className={inputClass} value={form.supplier_name} onChange={e => set('supplier_name', e.target.value)} />
                  </div>
                </div>
                {/* Supplier Record Only — bookmark mode. When ticked the
                    supplier is saved as a reference record only: no
                    emails fire, no portal access, no compliance workflow
                    runs. The contact / risk flag / payment terms
                    requirements are dropped below so staff can save with
                    minimal data. Toggle off later if the relationship
                    becomes active. */}
                <label className="flex items-start gap-3 mt-4 p-4 bg-white border border-[#DADCCD] rounded-xl cursor-pointer">
                  <input
                    type="checkbox"
                    checked={Boolean(form.is_record_only)}
                    onChange={e => set('is_record_only', e.target.checked)}
                    className="mt-0.5 accent-active-green" />
                  <span className="font-['Metropolis'] text-sm text-fusion-mid-grey">
                    Supplier Record Only
                    <span className="block font-['Metropolis'] text-[11px] text-[#51534A] mt-0.5">
                      Save the supplier's details for reference only. No emails will be sent to them, no supplier portal access will be granted, and contact / risk flag / payment terms are optional. Toggle off later if they become an active supplier.
                    </span>
                  </span>
                </label>
                {/* Risk Flag — required at creation. Dictates which
                    compliance documents are mandatory for this supplier. */}
                <p className={subSectionClass}>Risk Flag *</p>
                <p className="font-['Metropolis'] text-xs text-[#51534A] mb-3 -mt-2">Determines which compliance documents this supplier must complete. Configurable from Admin → Risk Flag Configuration.</p>
                <div className="flex gap-3">
                  {[
                    { key: 'high',   label: 'High Risk',   accent: '#E0290A', activeText: '#F9FCF1' },
                    { key: 'medium', label: 'Medium Risk', accent: '#E89A2E', activeText: '#131313' },
                    { key: 'low',    label: 'Low Risk',    accent: '#92D400', activeText: '#131313' },
                  ].map(opt => {
                    const isActive = form.risk_flag === opt.key;
                    return (
                      <button
                        key={opt.key}
                        type="button"
                        onClick={() => set('risk_flag', opt.key)}
                        className="inline-flex items-center gap-2 font-['Metropolis'] text-sm font-semibold tracking-[1px] uppercase px-5 py-2.5 rounded-lg transition-colors"
                        style={isActive
                          ? { background: opt.accent, color: opt.activeText }
                          : { background: '#EEF1E6', color: '#51534A', border: '1px solid #DDE1D0' }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                          <path d="M5 3a1 1 0 011 1v17a1 1 0 11-2 0V4a1 1 0 011-1z" />
                          <path d="M7 4h11.382a1 1 0 01.894 1.447L17.118 9.5l2.158 4.053A1 1 0 0118.382 15H7V4z" />
                        </svg>
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
                <p className={subSectionClass}>Main Contact (compulsory)</p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div>
                    <label className={labelClass}>Main Contact Name *</label>
                    <input className={inputClass} value={form.main_contact_name} onChange={e => set('main_contact_name', e.target.value)} />
                  </div>
                  <div>
                    <label className={labelClass}>Main Contact Email *</label>
                    <input type="email" className={inputClass} value={form.main_contact_email} onChange={e => set('main_contact_email', e.target.value)} />
                  </div>
                  <div>
                    <label className={labelClass}>Main Contact Phone *</label>
                    <input className={inputClass} value={form.main_contact_phone} onChange={e => set('main_contact_phone', e.target.value)} />
                  </div>
                </div>
                <p className={subSectionClass}>Secondary Contact (optional)</p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div>
                    <label className={labelClass}>Secondary Contact Name</label>
                    <input className={inputClass} value={form.secondary_contact_name} onChange={e => set('secondary_contact_name', e.target.value)} />
                  </div>
                  <div>
                    <label className={labelClass}>Secondary Contact Email</label>
                    <input type="email" className={inputClass} value={form.secondary_contact_email} onChange={e => set('secondary_contact_email', e.target.value)} />
                  </div>
                  <div>
                    <label className={labelClass}>Secondary Contact Phone</label>
                    <input className={inputClass} value={form.secondary_contact_phone} onChange={e => set('secondary_contact_phone', e.target.value)} />
                  </div>
                </div>

                <p className={sectionClass}>Business Context</p>
                <div className="space-y-6">
                  <div>
                    <label className={labelClass}>What goods/services will this supplier provide?</label>
                    <AutoSizeTextarea minRows={3} className={inputClass} value={form.goods_services_description} onChange={e => set('goods_services_description', e.target.value)} />
                  </div>
                  <div>
                    <label className={labelClass}>Why is a new supplier required?</label>
                    <AutoSizeTextarea minRows={3} className={inputClass} value={form.reason_for_new_supplier} onChange={e => set('reason_for_new_supplier', e.target.value)} />
                  </div>
                  <div>
                    <label className={labelClass}>Impact if this supplier is not set up?</label>
                    <AutoSizeTextarea minRows={3} className={inputClass} value={form.impact_if_not_setup} onChange={e => set('impact_if_not_setup', e.target.value)} />
                  </div>
                  <div>
                    <label className={labelClass}>Who evaluated supplier options?</label>
                    <AutoSizeTextarea minRows={3} className={inputClass} value={form.who_evaluated} onChange={e => set('who_evaluated', e.target.value)} />
                  </div>
                </div>

                <p className={sectionClass}>Project & Spend</p>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className={labelClass}>Initial Project / Cost Centre</label>
                    <input className={inputClass} value={form.initial_project_cost_centre} onChange={e => set('initial_project_cost_centre', e.target.value)} />
                  </div>
                  <div>
                    <label className={labelClass}>Expected Duration</label>
                    <select className={inputClass} value={form.expected_duration} onChange={e => set('expected_duration', e.target.value)}>
                      <option value="">Select...</option>
                      <option value="One off">One off</option>
                      <option value="Short term">Short term</option>
                      <option value="Ongoing">Ongoing</option>
                    </select>
                  </div>
                  <div>
                    <label className={labelClass}>Estimated Value of First Order</label>
                    <MoneyInput value={form.estimated_first_order} onChange={(v) => set('estimated_first_order', v)} min="0" />
                  </div>
                  <div>
                    <label className={labelClass}>Estimated Annual / Ongoing Spend</label>
                    <MoneyInput value={form.estimated_annual_spend} onChange={(v) => set('estimated_annual_spend', v)} min="0" />
                  </div>
                </div>

                <p className={sectionClass}>Commercial</p>
                <div>
                  <label className={labelClass}>Supplier Payment Terms *</label>
                  <input className={inputClass} value={form.payment_terms} onChange={e => set('payment_terms', e.target.value)} placeholder="e.g. 30 days from invoice" />
                </div>

                <div className="mt-8 p-4 border border-line bg-white">
                  <p className="font-['Metropolis'] text-xs font-semibold text-[#51534A] mb-2">Coming after save</p>
                  <p className="font-['Metropolis'] text-sm text-fusion-mid-grey">Attachments, the Finance Use Only section and the "Send Supplier Set-Up Form" button are available on the supplier detail page once the supplier has been saved.</p>
                </div>
              </>
            )}

            {activeTab === 'supplier' && (
              <>
                <p className={sectionClass}>Company Information</p>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className={labelClass}>Account Code</label>
                    <input className={inputClass} value={form.account_code} onChange={e => set('account_code', e.target.value)} />
                  </div>
                  <div>
                    <label className={labelClass}>Address Line 1</label>
                    <input className={inputClass} value={form.address_line_1} onChange={e => set('address_line_1', e.target.value)} />
                  </div>
                  <div>
                    <label className={labelClass}>Address Line 2</label>
                    <input className={inputClass} value={form.address_line_2} onChange={e => set('address_line_2', e.target.value)} />
                  </div>
                  <div>
                    <label className={labelClass}>Address Line 3</label>
                    <input className={inputClass} value={form.address_line_3} onChange={e => set('address_line_3', e.target.value)} />
                  </div>
                  <div>
                    <label className={labelClass}>Town</label>
                    <input className={inputClass} value={form.town} onChange={e => set('town', e.target.value)} />
                  </div>
                  <div>
                    <label className={labelClass}>Postcode</label>
                    <input className={inputClass} value={form.postcode} onChange={e => set('postcode', e.target.value)} />
                  </div>
                  <div>
                    <label className={labelClass}>Region</label>
                    <select className={inputClass} value={form.region} onChange={e => set('region', e.target.value)}>
                      <option value="">Select region...</option>
                      {regions.map(r => <option key={r.value} value={r.value}>{r.value}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={labelClass}>Phone</label>
                    <input className={inputClass} value={form.phone} onChange={e => set('phone', e.target.value)} />
                  </div>
                  <div>
                    <label className={labelClass}>Website</label>
                    <input className={inputClass} value={form.website} onChange={e => set('website', e.target.value)} />
                  </div>
                  <div>
                    <label className={labelClass}>Category</label>
                    <select className={inputClass} value={form.category} onChange={e => set('category', e.target.value)}>
                      <option value="">Select category...</option>
                      {categories.map(c => <option key={c.value} value={c.value}>{c.value}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={labelClass}>Sub Category</label>
                    <input className={inputClass} value={form.sub_category} onChange={e => set('sub_category', e.target.value)} />
                  </div>
                  <div>
                    <label className={labelClass}>Analysis Code 1</label>
                    <input className={inputClass} value={form.analysis_code_1} onChange={e => set('analysis_code_1', e.target.value)} />
                  </div>
                  <div>
                    <label className={labelClass}>Analysis Code 2</label>
                    <input className={inputClass} value={form.analysis_code_2} onChange={e => set('analysis_code_2', e.target.value)} />
                  </div>
                  <div>
                    <label className={labelClass}>Source</label>
                    <input className={inputClass} value={form.source} onChange={e => set('source', e.target.value)} />
                  </div>
                </div>

                <p className={sectionClass}>Status</p>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className={labelClass}>Do Not Use Status</label>
                    <select className={inputClass} value={form.do_not_use_status} onChange={e => set('do_not_use_status', e.target.value)}>
                      <option value="">Select...</option>
                      <option>Do Not Use</option>
                      <option>Use With Caution</option>
                      <option>Cleared for Use</option>
                    </select>
                  </div>
                  <div>
                    <label className={labelClass}>Preferred Supplier Status</label>
                    <select className={inputClass} value={form.preferred_supplier} onChange={e => set('preferred_supplier', e.target.value)}>
                      <option value="">Select...</option>
                      <option>Preferred Supplier</option>
                      <option>Approved Supplier</option>
                      <option>Under Review</option>
                      <option>Not Preferred</option>
                    </select>
                  </div>
                  <div>
                    <label className={labelClass}>New Supplier Form Received</label>
                    <select className={inputClass} value={form.new_supplier_form_received} onChange={e => set('new_supplier_form_received', e.target.value)}>
                      <option value="">Select...</option>
                      <option>Yes</option>
                      <option>No</option>
                      <option>Sent to Venue/Supplier to complete</option>
                    </select>
                  </div>
                  <div>
                    <label className={labelClass}>NDA Signed and Returned</label>
                    <select className={inputClass} value={form.nda_signed} onChange={e => set('nda_signed', e.target.value)}>
                      <option value="">Select...</option>
                      <option>Yes</option>
                      <option>No</option>
                      <option>Not Required</option>
                    </select>
                  </div>
                  <div>
                    <label className={labelClass}>H&S Questionnaire Returned</label>
                    <select className={inputClass} value={form.hs_questionnaire_returned} onChange={e => set('hs_questionnaire_returned', e.target.value)}>
                      <option value="">Select...</option>
                      <option>Yes</option>
                      <option>No</option>
                      <option>Not Required</option>
                    </select>
                  </div>
                  <div>
                    <label className={labelClass}>Code of Conduct Agreed and Returned</label>
                    <select className={inputClass} value={form.code_of_conduct_returned} onChange={e => set('code_of_conduct_returned', e.target.value)}>
                      <option value="">Select...</option>
                      <option>Yes</option>
                      <option>No</option>
                      <option>Not Required</option>
                    </select>
                  </div>
                </div>

                <p className={sectionClass}>Ratings</p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {[['rating_general', 'General'], ['rating_price', 'Price'], ['rating_quality', 'Quality']].map(([field, label]) => (
                    <div key={field}>
                      <label className={labelClass}>{label}</label>
                      <div className="flex gap-1 mt-1">
                        {[1,2,3,4,5,6,7,8,9,10].map(n => (
                          <button key={n} type="button"
                            onClick={() => set(field, form[field] === n ? '' : n)}
                            className="text-2xl leading-none transition-colors"
                            style={{color: n <= (form[field] || 0) ? '#92D400' : '#D4D9C6'}}>★</button>
                        ))}
                      </div>
                      {form[field] && <p className="font-['Metropolis'] text-xs text-[#51534A] mt-1">{form[field]} / 10</p>}
                    </div>
                  ))}
                </div>

                <p className={sectionClass}>Notes</p>
                <div className="space-y-6">
                  <div>
                    <label className={labelClass}>General Notes</label>
                    <AutoSizeTextarea minRows={4} className={inputClass} value={form.notes_general} onChange={e => set('notes_general', e.target.value)} />
                  </div>
                  <div>
                    <label className={labelClass}>Previous Work</label>
                    <AutoSizeTextarea minRows={4} className={inputClass} value={form.notes_previous_work} onChange={e => set('notes_previous_work', e.target.value)} />
                  </div>
                  <div>
                    <label className={labelClass}>Relationship Notes</label>
                    <AutoSizeTextarea minRows={4} className={inputClass} value={form.notes_relationship} onChange={e => set('notes_relationship', e.target.value)} />
                  </div>
                </div>

                {isFabricator && (
                  <>
                    <p className={sectionClass}>Fabricator Capabilities</p>
                    <div className="grid grid-cols-2 gap-4">
                      {[
                        ['fab_carpentry', 'Carpentry'],
                        ['fab_metalwork', 'Metal Work'],
                        ['fab_solid_surface', 'Solid Surface'],
                        ['fab_print_graphics', 'Print / Graphics'],
                        ['fab_cnc_cutting', 'CNC Cutting'],
                        ['fab_wet_spraying', 'Wet Spraying'],
                        ['fab_plastics', 'Plastics'],
                        ['fab_3d_printing', '3D Printing'],
                        ['fab_development_capability', 'Development Capability'],
                        ['fab_dmx', 'DMX'],
                        ['fab_electrical_engineering', 'Electrical Engineering'],
                        ['fab_meu_coachbuilding', 'MEU / Coachbuilding'],
                      ].map(([field, label]) => (
                        <label key={field} className={checkboxClass}>
                          <input type="checkbox" checked={form[field]} onChange={e => set(field, e.target.checked)}
                            className="w-4 h-4 accent-active-green" />
                          <span className="font-['Metropolis'] text-sm text-fusion-mid-grey">{label}</span>
                        </label>
                      ))}
                    </div>
                  </>
                )}

                {isVenue && (
                  <>
                    <p className={sectionClass}>Venue Information</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                      {[
                        ['venue_high_footfall', 'High Footfall'],
                        ['venue_test_drive_events', 'Test Drive Events'],
                        ['venue_press_launch', 'Press Launch'],
                      ].map(([field, label]) => (
                        <label key={field} className={checkboxClass}>
                          <input type="checkbox" checked={form[field]} onChange={e => set(field, e.target.checked)}
                            className="w-4 h-4 accent-active-green" />
                          <span className="font-['Metropolis'] text-sm text-fusion-mid-grey">{label}</span>
                        </label>
                      ))}
                    </div>
                    <div className="grid grid-cols-2 gap-6">
                      {[
                        ['venue_type', 'Venue Type'],
                        ['venue_number_of_bedrooms', 'Number of Bedrooms'],
                        ['venue_inc_ex_vat', 'Inc or Ex VAT'],
                        ['venue_commission_percentage', 'Commission Percentage'],
                        ['venue_ddr_rate', 'DDR Rate'],
                        ['venue_24hr_rate', '24 Hour Rate'],
                        ['venue_car_parking', 'Onsite Car Parking'],
                        ['venue_car_parking_cost', 'Car Parking Cost'],
                        ['venue_space_hire_rate', 'Space Hire Rate'],
                        ['venue_name_of_space', 'Name of Space'],
                      ].map(([field, label]) => (
                        <div key={field}>
                          <label className={labelClass}>{label}</label>
                          <input className={inputClass} value={form[field]} onChange={e => set(field, e.target.value)} />
                        </div>
                      ))}
                      {[
                        ['venue_meeting_room_sizes', 'Meeting Room Sizes / Stand Space'],
                        ['venue_test_drive_environment', 'Test Drive Environment'],
                        ['venue_preferred_suppliers', 'Venue Preferred Suppliers'],
                        ['venue_access_information', 'Access Information / Times / Considerations'],
                        ['venue_onsite_rules', 'Onsite Rules and Regs'],
                        ['venue_automotive_permitted', 'Automotive Events Permitted'],
                        ['venue_power', 'Power'],
                        ['venue_external_water_power', 'External Water and Power'],
                        ['venue_ev_chargers', 'EV Car Chargers'],
                        ['venue_vehicles_displayed_inside', 'Can Vehicles be Displayed Inside'],
                        ['venue_commissionable_items', 'Commissionable Items'],
                        ['venue_ddr_inclusions', 'DDR Inclusions'],
                        ['venue_24hr_inclusions', '24 Hour Rate Inclusions'],
                        ['venue_additional_catering', 'Additional Catering Costs'],
                        ['venue_av_inclusions', 'AV Inclusions'],
                        ['venue_cost_additional_av', 'Cost of Additional AV'],
                        ['venue_valeting_area', 'Valeting Area'],
                        ['venue_search_criteria', 'Search Criteria'],
                      ].map(([field, label]) => (
                        <div key={field} className="col-span-2">
                          <label className={labelClass}>{label}</label>
                          <AutoSizeTextarea minRows={3} className={inputClass} value={form[field]} onChange={e => set(field, e.target.value)} />
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </>
            )}

            <div className="mt-10 pt-6 border-t border-line flex justify-end gap-3">
              <button onClick={() => navigate('/suppliers')} className="bg-white rounded-r-btn border border-line text-fusion-mid-grey font-['Metropolis'] text-sm font-semibold px-6 py-3 hover:border-[#DADCCD] transition-colors">Cancel</button>
              <button onClick={handleSave} disabled={saving} className="bg-[#92D400] text-[#131313] rounded-r-btn font-['Metropolis'] text-sm font-semibold px-6 py-3 hover:bg-[#6a9a00] transition-colors disabled:opacity-50">
                {saving ? 'Saving...' : 'Save Supplier'}
              </button>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}
